import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import _ from 'lodash';
import { listShipmentUnits } from '../API/shipmentUnit';
import { getPaginatedResults } from '../utils/pagination';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

function labelForUnit(option) {
  if (!option) return '';
  const unitNumber = option.unit_number != null ? String(option.unit_number).trim() : '';
  const serial = option.serial_number != null ? String(option.serial_number).trim() : '';
  const id = option.id != null ? `#${option.id}` : '';
  if (unitNumber && serial && id) return `${unitNumber} · ${serial} (${id})`;
  if (unitNumber && serial) return `${unitNumber} · ${serial}`;
  if (unitNumber && id) return `${unitNumber} (${id})`;
  if (unitNumber) return unitNumber;
  if (serial && id) return `${serial} (${id})`;
  return serial || id || '';
}

export default function ShipmentUnitOptionList({
  onChange,
  required,
  defaultValue,
  disabled = false,
  label,
  errorMessage,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListUnits = hasPermission(PERMISSIONS.SHIPMENT_UNITS_LIST);
  const resolvedLabel = label ?? t('widgets.shipmentUnit');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const fetchUnits = async (search) => {
    if (!canListUnits) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await listShipmentUnits({
        page: 1,
        page_size: 30,
        search: search || '',
        deleted: false,
        ordering: 'serial_number',
      });
      setOptions(getPaginatedResults(response?.data));
    } catch (err) {
      console.error('ShipmentUnitOptionList:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(_.debounce(fetchUnits, 300), []);

  useEffect(() => {
    if (inputValue) {
      debouncedFetch(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue, debouncedFetch]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: '8px',
        },
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: '#555',
          marginBottom: '6px',
          fontSize: '14px',
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        onInputChange={handleInputChange}
        value={defaultValue == null || defaultValue === '' ? null : defaultValue}
        open={open}
        onOpen={() => {
          setOpen(true);
          fetchUnits(inputValue || '');
        }}
        onClose={() => setOpen(false)}
        placeholder={t('widgets.searchBySerial')}
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canListUnits}
        onChange={onChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionLabel={(option) => labelForUnit(option)}
        noOptionsText={
          !canListUnits ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            px={2}
          >
            <span>{t('widgets.typeToSearch')}</span>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={() => window.open('/shipment/units', '_blank')}
            >
              {t('widgets.manageUnits')}
            </Button>
          </Box>
          )
        }
        loadingText={t('actions.loading')}
        filterOptions={(opts) => opts}
        endDecorator={
          loading && (
            <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
          )
        }
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: '#5f09c2',
          },
          '&:focus': {
            borderColor: '#5f09c2',
            outline: 'none',
            boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
          ...(errorMessage
            ? {
                borderColor: 'var(--joy-palette-danger-500, #c41c1c)',
                boxShadow: '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)',
              }
            : {}),
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
    </Box>
  );
}
